import React from 'react';

const PrivacyPolicy = () => {
  const lastUpdated = 'August 17, 2026';

  const sections = [
    {
      title: '1. Information We Collect',
      content: [
        {
          subtitle: 'Account Information',
          text: 'When you register or log in, we collect your name, email address, phone number, and role within your organization.',
        },
        {
          subtitle: 'Usage Data',
          text: 'We collect information about how you interact with the app, including pages visited, features used, and actions performed.',
        },
        {
          subtitle: 'Device Information',
          text: 'We may collect device identifiers, operating system version, app version, and IP address for security and analytics purposes.',
        },
        {
          subtitle: 'Location Data',
          text: 'With your permission, we may collect location data for attendance tracking features (check-in/check-out). You can disable this in your device settings.',
        },
      ],
    },
    {
      title: '2. How We Use Your Information',
      content: [
        {
          text: 'We use the information we collect to:',
          list: [
            'Provide, operate, and maintain the SparkCRM application',
            'Manage employee attendance, payroll, tasks, and leads',
            'Send notifications related to tasks, leaves, and assignments',
            'Improve and personalize your experience',
            'Ensure security and prevent unauthorized access',
            'Comply with legal obligations',
          ],
        },
      ],
    },
    {
      title: '3. Data Sharing',
      content: [
        {
          text: 'We do not sell, trade, or rent your personal information to third parties. We may share data only in the following cases:',
          list: [
            'With your organization\'s administrators as part of the CRM functionality',
            'With service providers who assist in operating our platform (e.g., hosting, analytics) under strict confidentiality agreements',
            'When required by law or to protect our legal rights',
          ],
        },
      ],
    },
    {
      title: '4. Data Security',
      content: [
        {
          text: 'We implement industry-standard security measures to protect your data, including:',
          list: [
            'Encrypted data transmission using HTTPS/TLS',
            'Secure authentication using JWT tokens',
            'Regular security audits and monitoring',
            'Access controls limiting data access to authorized personnel only',
          ],
        },
      ],
    },
    {
      title: '5. Data Retention',
      content: [
        {
          text: 'We retain your personal data for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data by contacting us at support@sparkcrm.in. Some data may be retained for legal or audit purposes.',
        },
      ],
    },
    {
      title: '6. Your Rights',
      content: [
        {
          text: 'You have the right to:',
          list: [
            'Access the personal data we hold about you',
            'Request correction of inaccurate data',
            'Request deletion of your personal data',
            'Withdraw consent for location or notification permissions at any time via device settings',
            'Lodge a complaint with a data protection authority',
          ],
        },
      ],
    },
    {
      title: '7. Cookies & Local Storage',
      content: [
        {
          text: 'SparkCRM uses browser local storage and session storage to maintain authentication tokens and user preferences. We do not use third-party tracking cookies. You can clear stored data at any time through your browser or device settings.',
        },
      ],
    },
    {
      title: '8. Children\'s Privacy',
      content: [
        {
          text: 'SparkCRM is a business management application intended for use by adults in a professional context. We do not knowingly collect personal information from individuals under the age of 18.',
        },
      ],
    },
    {
      title: '9. Third-Party Services',
      content: [
        {
          text: 'The app may integrate with third-party services such as WhatsApp for messaging. These services have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of third-party services.',
        },
      ],
    },
    {
      title: '10. Changes to This Policy',
      content: [
        {
          text: 'We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the "Last Updated" date at the top of this page. Continued use of the app after changes constitutes acceptance of the updated policy.',
        },
      ],
    },
    {
      title: '11. Contact Us',
      content: [
        {
          text: 'If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us:',
          list: [
            'Email: support@sparkcrm.in',
            'Phone: +91 12345 67890',
            'Website: https://sparkcrm.in',
          ],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 font-sans">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-2xl">S</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-500">SparkCRM — Business Management Platform</p>
          <p className="mt-1 text-xs text-gray-400">Last Updated: {lastUpdated}</p>
        </div>

        {/* Intro */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-6">
          <p className="text-sm text-blue-800 leading-relaxed">
            SparkCRM ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy
            explains how we collect, use, store, and share information when you use our mobile application
            and web platform. By using SparkCRM, you agree to the practices described in this policy.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-base font-bold text-gray-800 mb-3">{section.title}</h2>
              {section.content.map((block, blockIndex) => (
                <div key={blockIndex} className="mb-3 last:mb-0">
                  {block.subtitle && (
                    <p className="text-sm font-semibold text-gray-700 mb-1">{block.subtitle}</p>
                  )}
                  {block.text && (
                    <p className="text-sm text-gray-500 leading-relaxed">{block.text}</p>
                  )}
                  {block.list && (
                    <ul className="mt-2 space-y-1">
                      {block.list.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-2 text-sm text-gray-500">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer links */}
        <div className="mt-8 flex justify-center gap-6 text-xs text-gray-400">
          <a href="https://apk.sparkcrm.in/support" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 underline">
            Support
          </a>
          <a href="https://sparkcrm.in/terms" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 underline">
            Terms of Service
          </a>
          <a href="https://sparkcrm.in" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 underline">
            Website
          </a>
        </div>

        <p className="text-center text-xs text-gray-300 mt-4 mb-8">
          &copy; {new Date().getFullYear()} SparkCRM. All rights reserved.
        </p>

      </div>
    </div>
  );
};

export default PrivacyPolicy;
